import React from 'react';
import { Reveal } from '../Reveal';
import { Button } from '../Button';
import { PredictionChart } from '../chart/PredictionChart';
import { PredictionSlider } from '../PredictionSlider';
import { useLayout } from '../../contexts/LayoutContext';
type PredictionScreenProps = {
  estimate: number;
  touched: boolean;
  onEstimate: (value: number) => void;
  onNext: () => void;
};
export function PredictionScreen({
  estimate,
  touched,
  onEstimate,
  onNext
}: PredictionScreenProps) {
  const {
    compact,
    wide
  } = useLayout();
  const cta = <Button fullWidth={compact} onClick={onNext} disabled={!touched} data-id="element-130" data-mp="df9kabctdppn">
      Reveal result
    </Button>;
  const headline = <Reveal data-id="element-131" data-mp="3fsi43wrttyf">
      <h2 className={`max-w-md font-display font-light tracking-[-0.03em] text-ink ${compact ? 'text-[2.1rem] leading-[1.08]' : 'text-5xl leading-[1.05]'}`} data-id="element-132" data-mp="3fisihfn1ot9">
        What difference would you expect at Week&nbsp;52?
      </h2>
      <p className={`max-w-sm leading-relaxed text-muted ${compact ? 'mt-3 text-sm' : 'mt-5 text-[15px]'}`} data-id="element-133" data-mp="1xrpbw3iorbwn">
        Estimate how much less FVC decline you would expect with JASCAYD 18 mg versus placebo.
      </p>
    </Reveal>;

  // The estimate control: value readout + slider, kept together and separate from the chart's time axis
  const control = <Reveal delay={0.14} data-id="element-134" data-mp="26jz9kd12r406">
      <div className={`rounded-2xl bg-surface ${compact ? 'px-5 pb-4 pt-5' : 'max-w-md px-6 pb-5 pt-6'}`} data-id="element-135" data-mp="26jfztjlkvlx8">
        <div className="flex items-end justify-between gap-4" aria-live="polite" data-id="element-136" data-mp="1itlqvd4dqhi9">
          <div data-id="element-137" data-mp="1dvtofp63zczt">
            <p className="text-xs text-muted" data-id="element-138" data-mp="1dw3ibnu9y4as">Your estimated difference</p>
            <p className={`mt-1 font-display font-light leading-none tracking-[-0.04em] tabular-nums ${touched ? 'text-accent' : 'text-ink'} ${compact ? 'text-5xl' : 'text-7xl'}`} data-id="element-139" data-mp="1rutbisdz3jtv">
              {touched ? estimate : '—'}
              <span className="ml-2 text-[0.4em] text-muted" data-id="element-140" data-mp="2z8dbv4z74mhj">mL</span>
            </p>
          </div>
          <p className="pb-1 text-right text-xs text-faint" data-id="element-141" data-mp="1vxhcwzahdif8">{touched ? 'at Week 52' : 'Drag to set'}</p>
        </div>
        <div className="mt-4" data-id="element-142" data-mp="20uheoy0arz0j">
          <PredictionSlider value={estimate} touched={touched} onChange={onEstimate} data-id="element-143" data-mp="20xlsmn3ke94e" />
        </div>
      </div>
    </Reveal>;
  const chart = <Reveal delay={0.1} data-id="element-144" data-mp="29wqn9ic09iba">
      <PredictionChart estimate={estimate} touched={touched} data-id="element-145" data-mp="29vxgwigykuto" />
      <p className="mt-2 text-center text-xs text-faint" data-id="element-146" data-mp="1d6q2duerblz9">
        The gap between the lines at Week 52 reflects your estimate
      </p>
    </Reveal>;
  if (wide) {
    return <div className="grid min-h-full grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-center gap-20 py-10" data-id="element-147" data-mp="1m74sr86kvr9m">
        <div data-id="element-148" data-mp="1qy4o4901u1a0">
          {headline}
          <div className="mt-10" data-id="element-149" data-mp="1qyecxt9cy2e4">{control}</div>
          <div className="mt-8" data-id="element-150" data-mp="ufovmk7eam55">{cta}</div>
        </div>
        {chart}
      </div>;
  }
  return <div className={`flex min-h-full flex-col ${compact ? 'py-6' : 'justify-center py-10'}`} data-id="element-151" data-mp="yh228wibatd4">
      {headline}
      <div className={compact ? 'mt-5' : 'mt-8'} data-id="element-152" data-mp="qyawikyssb60">{chart}</div>
      <div className={compact ? 'mt-5' : 'mt-8'} data-id="element-153" data-mp="qvookk7zvtn9">{control}</div>
      <div className={compact ? 'mt-auto pt-6' : 'mt-10'} data-id="element-154" data-mp="lzzp0gvz6cqy">{cta}</div>
    </div>;
}